import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Divider,
    TextField,
    IconButton,
    InputAdornment,
    Badge,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Popper,
    ClickAwayListener,
    Button,
    Alert,
    Collapse,
} from "@mui/material";
import {
    Send,
    Search,
    Phone,
    Chat,
    MoreVert,
    AttachFile,
    EmojiEmotions,
    ArrowBack,
    Person,
    KeyboardArrowDown,
    Fullscreen,
    Close,
    Info,
} from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import TemplateMessageBubble from "./testMessage/TemplateMessageBubble";

// Format phone number for display (add spaces for readability)
const formatPhoneForDisplay = (phoneNumber) => {
    if (!phoneNumber) return "";
    // Remove all non-digit characters first
    const digits = phoneNumber.replace(/\D/g, "");
    // Format: +XX XXX XXX XXXX (for 10+ digits) or keep as is for shorter
    if (digits.length >= 10) {
        const countryCode = digits.length > 10 ? `+${digits.slice(0, -10)} ` : "";
        const number = digits.slice(-10);
        return `${countryCode}${number.slice(0, 5)} ${number.slice(5)}`;
    }
    return phoneNumber;
};

const mockMessages = {
    1: [
        {
            id: 1,
            text: "Hello, I need help with my order",
            sender: "user",
            timestamp: "10:25 AM",
        },
        {
            id: 2,
            text: "Hello! I'd be happy to help you with your order. Can you please provide your order number?",
            sender: "bot",
            timestamp: "10:26 AM",
        },
        {
            id: 3,
            text: "My order number is ORD-12345",
            sender: "user",
            timestamp: "10:27 AM",
        },
        {
            id: 4,
            text: "Thank you! Let me check that for you. Your order is currently being processed and will be shipped within 2-3 business days.",
            sender: "bot",
            timestamp: "10:28 AM",
        },
        {
            id: 5,
            text: "That's great! When can I expect delivery?",
            sender: "user",
            timestamp: "10:30 AM",
        },
    ],
    2: [
        {
            id: 1,
            text: "Hi there!",
            sender: "user",
            timestamp: "9:10 AM",
        },
        {
            id: 2,
            text: "Hello! How can I assist you today?",
            sender: "bot",
            timestamp: "9:11 AM",
        },
        {
            id: 3,
            text: "Thank you for your response",
            sender: "user",
            timestamp: "9:15 AM",
        },
    ],
    3: [
        {
            id: 1,
            text: "When will my order arrive?",
            sender: "user",
            timestamp: "Yesterday, 3:45 PM",
        },
    ],
    4: [
        {
            id: 1,
            text: "I have a question about your services",
            sender: "user",
            timestamp: "Yesterday, 2:30 PM",
        },
        {
            id: 2,
            text: "Of course! What would you like to know?",
            sender: "bot",
            timestamp: "Yesterday, 2:31 PM",
        },
    ],
    5: [
        {
            id: 1,
            text: "Great service!",
            sender: "user",
            timestamp: "2 days ago, 5:20 PM",
        },
        {
            id: 2,
            text: "Thank you so much for your feedback! We're glad you're happy with our service.",
            sender: "bot",
            timestamp: "2 days ago, 5:21 PM",
        },
    ],
};

// Professional emojis for business chat
const professionalEmojis = [
    "🙂", "😊", "😄", "😃", "😁", "🙏", "👍", "👎", "👌", "✌️",
    "🤝", "👏", "🙌", "💪", "✅", "❌", "✔️", "⭐", "🌟", "💯",
];

const WhatsappChatbot = () => {
    const [selectedContact, setSelectedContact] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [messageInput, setMessageInput] = useState("");
    const [showChatView, setShowChatView] = useState(false); // For mobile view
    const [loading, setLoading] = useState(false);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [messages, setMessages] = useState({});
    const [recipients, setRecipients] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
    });
    const [conversationPagination, setConversationPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
    });
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastMessageId, setLastMessageId] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const contactsListRef = useRef(null);
    const messagesListRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const notify = useNotification();
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiButtonRef = useRef(null);
    const [templateDetailsCache, setTemplateDetailsCache] = useState({}); // Cache template details by template_id
    const [loadingTemplates, setLoadingTemplates] = useState({}); // Track loading state per template
    const [showDeprecationNotice, setShowDeprecationNotice] = useState(true);

    // Fetch recipients from API
    const fetchRecipients = async (page = 1, append = false) => {
        if (loadingMore && append) return; // Prevent multiple simultaneous requests
        
        if (append) {
            setLoadingMore(true);
        } else {
            setLoadingRecipients(true);
        }

        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/chat/messages/incoming`,
                {
                    params: {
                        page: page,
                        per_page: 50,
                    },
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.success) {
                const newRecipients = response.data.data.map((item) => ({
                    id: item.id,
                    recipient: item.recipient,
                    phoneNumber: formatPhoneForDisplay(item.recipient),
                    message_id: item.message_id,
                    phone_number_id: item.phone_number_id,
                    updated_at: item.updated_at,
                    timestamp: new Date(item.updated_at).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    unreadCount: 0, // You can add logic to calculate unread count
                }));

                if (append) {
                    setRecipients((prev) => [...prev, ...newRecipients]);
                } else {
                    setRecipients(newRecipients);
                }

                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error("Error fetching recipients:", error);
            notify(
                error.response?.data?.message || "Failed to fetch recipients",
                "error"
            );
        } finally {
            setLoadingRecipients(false);
            setLoadingMore(false);
        }
    };

    // Load recipients on component mount
    useEffect(() => {
        fetchRecipients(1, false);
    }, []);

    // Auto-refetch conversation messages every 10 seconds when a contact is selected
    useEffect(() => {
        if (!selectedContact) return;

        const contact = recipients.find((c) => c.id === selectedContact);
        if (!contact) return;

        // Set up interval to refetch messages every 10 seconds
        const intervalId = setInterval(() => {
            // Only refetch if not currently loading messages
            if (!loadingMessages && !loadingMoreMessages) {
                fetchConversationMessages(selectedContact, contact.recipient, 1, false, true);
            }
        }, 10000); // 10 seconds

        // Cleanup interval on unmount or when selected contact changes
        return () => {
            clearInterval(intervalId);
        };
    }, [selectedContact, recipients, loadingMessages, loadingMoreMessages]);

    // Handle scroll for pagination
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

        if (
            isNearBottom &&
            !loadingMore &&
            pagination.current_page < pagination.last_page
        ) {
            fetchRecipients(pagination.current_page + 1, true);
        }
    };

    const filteredContacts = recipients.filter((contact) =>
        contact.recipient.includes(searchQuery.replace(/\D/g, "")) ||
        contact.phoneNumber.includes(searchQuery)
    );

    const currentMessages = selectedContact ? messages[selectedContact] || [] : [];

    // Fetch conversation messages for a recipient
    const fetchConversationMessages = async (recipientId, recipientPhone, page = 1, append = false, isAutoRefetch = false) => {
        if (loadingMoreMessages && append) return;

        // Store previous message count and last message ID before refetch
        const previousMessages = messages[recipientId] || [];
        const previousLastMessageId = previousMessages.length > 0 
            ? previousMessages[previousMessages.length - 1].id 
            : null;
        
        // Check scroll position DIRECTLY from the DOM element (not from state which might be stale)
        let savedScrollTop = null;
        let userIsAtBottom = true;
        if (isAutoRefetch && messagesListRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
            savedScrollTop = scrollTop;
            // Check if user is actually at bottom (within 50px threshold for more accuracy)
            userIsAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        }

        if (append) {
            setLoadingMoreMessages(true);
        } else {
            setLoadingMessages(true);
        }

        try {
            const phoneNumber = formatPhoneNumber(recipientPhone);
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/chat/messages/conversation`,
                {
                    params: {
                        recipient: phoneNumber,
                        page: page,
                        per_page: 50,
                    },
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.success) {
                const { incoming = [], outgoing = [] } = response.data.data || {};

                // Helper function to process a message
                const processMessage = (msg, senderType) => {
                    // Check if this is a template message by checking template_id
                    // Template messages have template_id, media_link, media_type, and payload
                    const hasTemplateId = !!msg.template_id;
                    
                    // Parse payload if it exists
                    // Payload contains components array with parameters for header/body
                    let parsedPayload = null;
                    if (msg.payload) {
                        try {
                            parsedPayload = typeof msg.payload === 'string' 
                                ? JSON.parse(msg.payload) 
                                : msg.payload;
                        } catch (e) {
                            console.error("Error parsing payload:", e);
                            parsedPayload = null;
                        }
                    }

                    // Build message object
                    const messageObj = {
                        id: msg.id,
                        text: msg.message_content || "",
                        sender: senderType,
                        timestamp: new Date(msg.created_at).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                        }),
                        created_at: msg.created_at,
                        message_id: msg.message_id,
                        status: msg.status,
                    };

                    // If template_id exists, this is a template message
                    // Template messages need:
                    // - template_id: to fetch template details
                    // - media_link: for header media (if header has IMAGE/VIDEO/DOCUMENT)
                    // - media_type: type of media (image/video/document)
                    // - payload: contains components with parameters for header/body text replacement
                    if (hasTemplateId) {
                        messageObj.is_template = true;
                        messageObj.template_id = msg.template_id;
                        messageObj.media_link = msg.media_link || null; // Cloudflare path for media
                        messageObj.media_type = msg.media_type || null; // image/video/document
                        messageObj.payload = parsedPayload; // Contains components with parameters
                    } else {
                        // Regular text message
                        messageObj.is_template = false;
                        messageObj.template_id = null;
                        messageObj.media_link = null;
                        messageObj.media_type = null;
                        messageObj.payload = null;
                    }

                    return messageObj;
                };

                // Merge and format messages
                const allMessages = [
                    ...incoming.map((msg) => processMessage(msg, "user")),
                    ...outgoing.map((msg) => processMessage(msg, "bot")),
                ];

                // Fetch template details for all messages with template_id
                const templateIds = new Set();
                allMessages.forEach((msg) => {
                    if (msg.template_id) {
                        templateIds.add(msg.template_id);
                    }
                });

                // Fetch all unique template details
                const templatePromises = Array.from(templateIds).map((templateId) =>
                    fetchTemplateDetails(templateId)
                );
                await Promise.all(templatePromises);

                // Sort by created_at (oldest first)
                allMessages.sort((a, b) => {
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateA - dateB;
                });

                if (append) {
                    // For pagination, prepend older messages
                    setMessages((prev) => {
                        const existingMessages = prev[recipientId] || [];
                        const combined = [...allMessages, ...existingMessages];
                        // Re-sort after combining
                        combined.sort((a, b) => {
                            const dateA = new Date(a.created_at);
                            const dateB = new Date(b.created_at);
                            return dateA - dateB;
                        });
                        return {
                            ...prev,
                            [recipientId]: combined,
                        };
                    });
                } else {
                    // Check if there are new messages (for auto-refetch)
                    if (isAutoRefetch && previousLastMessageId) {
                        const newMessages = allMessages.filter(
                            (msg) => msg.id > previousLastMessageId
                        );
                        
                        if (newMessages.length > 0) {
                            // If user is not at bottom, increment unread count
                            if (!userIsAtBottom) {
                                setUnreadCount((prev) => prev + newMessages.length);
                            }
                        }
                    }

                    setMessages((prev) => ({
                        ...prev,
                        [recipientId]: allMessages,
                    }));

                    // Only auto-scroll if:
                    // 1. Not an auto-refetch (initial load or manual refresh)
                    // 2. OR it's an auto-refetch but user was actually at bottom (checked from DOM)
                    if (!isAutoRefetch || (isAutoRefetch && userIsAtBottom)) {
                        // Scroll to bottom when new conversation loads or user was at bottom
                        setTimeout(() => {
                            if (messagesListRef.current) {
                                messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
                            }
                            setIsAtBottom(true);
                            setShowScrollButton(false);
                            if (isAutoRefetch) {
                                setUnreadCount(0); // Reset unread count when auto-scrolling
                            }
                        }, 100);
                    } else {
                        // User was not at bottom, preserve scroll position
                        // Use requestAnimationFrame for better timing
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                if (messagesListRef.current && savedScrollTop !== null) {
                                    // Restore the same scrollTop position
                                    // Since new messages are added at the bottom, scrollTop should remain the same
                                    messagesListRef.current.scrollTop = savedScrollTop;
                                }
                            }, 0);
                        });
                    }

                    // Update last message ID
                    if (allMessages.length > 0) {
                        const currentLastMessageId = allMessages[allMessages.length - 1].id;
                        // Update last message ID on initial load or when user is at bottom
                        if (!isAutoRefetch || userIsAtBottom) {
                            setLastMessageId(currentLastMessageId);
                        }
                    }
                }

                if (response.data.pagination) {
                    setConversationPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error("Error fetching conversation messages:", error);
            notify(
                error.response?.data?.message || "Failed to fetch messages",
                "error"
            );
        } finally {
            setLoadingMessages(false);
            setLoadingMoreMessages(false);
        }
    };

    // Format phone number: remove spaces, +, and keep only digits
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return "";
        // Remove all non-digit characters
        return phoneNumber.replace(/\D/g, "");
    };

    // Fetch template details
    const fetchTemplateDetails = async (templateId) => {
        if (!templateId) return null;
        
        // Check cache first
        if (templateDetailsCache[templateId]) {
            return templateDetailsCache[templateId];
        }

        // Check if already loading
        if (loadingTemplates[templateId]) {
            return null;
        }

        setLoadingTemplates((prev) => ({ ...prev, [templateId]: true }));

        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/details`,
                {
                    params: {
                        fields: "id,name,language,category,status,components,previous_category,parameter_format"
                    },
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.id) {
                const templateData = response.data;
                setTemplateDetailsCache((prev) => ({
                    ...prev,
                    [templateId]: templateData,
                }));
                return templateData;
            }
        } catch (error) {
            console.error(`Error fetching template details for ${templateId}:`, error);
        } finally {
            setLoadingTemplates((prev) => {
                const newState = { ...prev };
                delete newState[templateId];
                return newState;
            });
        }

        return null;
    };

    const handleSendMessage = async () => {
        if (!messageInput.trim()) {
            return;
        }

        const selectedContactData = recipients.find((c) => c.id === selectedContact);
        if (!selectedContactData) {
            notify("Contact not found", "error");
            return;
        }

        const phoneNumber = formatPhoneNumber(selectedContactData.recipient);
        if (!phoneNumber) {
            notify("Invalid phone number", "error");
            return;
        }

        setLoading(true);
        const messageText = messageInput.trim();

        try {
            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/chat/messages/send`,
                {
                    to: phoneNumber,
                    message: messageText,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (response.data) {
                // Add the sent message to the messages list
                const newMessage = {
                    id: Date.now(),
                    text: messageText,
                    sender: "bot", // Messages sent by us appear as bot
                    timestamp: new Date().toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    created_at: new Date().toISOString(),
                };

                setMessages((prev) => ({
                    ...prev,
                    [selectedContact]: [...(prev[selectedContact] || []), newMessage],
                }));

                notify("Message sent successfully", "success");
                setMessageInput("");
                
                // Scroll to bottom after sending message
                setTimeout(() => {
                    scrollToBottom();
                }, 100);
                
                // Optionally refresh conversation to get the actual message from API
                // Uncomment if you want to sync with backend immediately
                // const contact = recipients.find((c) => c.id === selectedContact);
                // if (contact) {
                //     setTimeout(() => {
                //         fetchConversationMessages(selectedContact, contact.recipient, 1, false);
                //     }, 1000);
                // }
            } else {
                throw new Error("Failed to send message");
            }
        } catch (error) {
            console.error("Error sending message:", error);
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to send message";
            notify(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !loading) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleContactSelect = (contactId) => {
        setSelectedContact(contactId);
        setUnreadCount(0);
        setIsAtBottom(true);
        setShowScrollButton(false);
        setLastMessageId(null);
        const contact = recipients.find((c) => c.id === contactId);
        if (contact) {
            // Fetch conversation messages when contact is selected
            fetchConversationMessages(contactId, contact.recipient, 1, false);
        }
        if (isMobile) {
            setShowChatView(true);
        }
    };

    // Handle scroll for conversation messages pagination
    const handleMessagesScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        
        // Check if user is at bottom (within 100px threshold)
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setIsAtBottom(isNearBottom);
        setShowScrollButton(!isNearBottom);
        
        // Reset unread count when user scrolls to bottom
        if (isNearBottom && unreadCount > 0) {
            setUnreadCount(0);
            // Update last message ID to current last message
            const currentMessages = messages[selectedContact] || [];
            if (currentMessages.length > 0) {
                setLastMessageId(currentMessages[currentMessages.length - 1].id);
            }
        }
        
        // Load more when scrolling to top (older messages)
        if (
            scrollTop < 100 &&
            !loadingMoreMessages &&
            conversationPagination.current_page < conversationPagination.last_page &&
            selectedContact
        ) {
            const contact = recipients.find((c) => c.id === selectedContact);
            if (contact) {
                fetchConversationMessages(
                    selectedContact,
                    contact.recipient,
                    conversationPagination.current_page + 1,
                    true
                );
            }
        }
    };

    // Scroll to bottom function
    const scrollToBottom = () => {
        if (messagesListRef.current) {
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
            setIsAtBottom(true);
            setShowScrollButton(false);
            setUnreadCount(0);
            // Update last message ID
            const currentMessages = messages[selectedContact] || [];
            if (currentMessages.length > 0) {
                setLastMessageId(currentMessages[currentMessages.length - 1].id);
            }
        }
    };

    const handleBackToContacts = () => {
        setShowChatView(false);
    };

    const handleEmojiClick = (emoji) => {
        setMessageInput((prev) => prev + emoji);
        setShowEmojiPicker(false);
    };

    const handleEmojiButtonClick = () => {
        setShowEmojiPicker((prev) => !prev);
    };

    return (
        <Box sx={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
            {/* Deprecation Notice */}
            <Collapse in={showDeprecationNotice}>
                <Alert
                    severity="info"
                    icon={<Info />}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setShowDeprecationNotice(false);
                            }}
                        >
                            <Close fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{
                        mt: 3,
                        mb: 2,
                        borderRadius: 2,
                        "& .MuiAlert-message": {
                            width: "100%",
                        },
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: "medium", mb: 0.5 }}>
                        Important Notice: Enhanced Chat Experience Available
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        We recommend using the <strong>Full Screen Chat Window</strong> for an improved experience with additional features. 
                        This current chat interface will be deprecated in a future update. 
                        Click the "Full Screen Chat Window" button above to access the enhanced version.
                    </Typography>
                </Alert>
            </Collapse>

            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    mb: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    ...WB_SX.card,
                }}
            >
                <Typography variant="h6" sx={{ ...WB_SX.pageTitle, mb: 0 }}>
                    WhatsApp Business Chat
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Fullscreen />}
                    onClick={() => {
                        window.open("/whatsapp-business/chat-window", "_blank");
                    }}
                    sx={WB_SX.primaryButton}
                >
                    Full Screen Chat Window
                </Button>
            </Box>

            {/* Main Container */}
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    gap: 2,
                    overflow: "hidden",
                    flexDirection: isMobile ? "column" : "row",
                }}
            >
                {/* Left Panel - Phone Numbers */}
                <Paper
                    elevation={2}
                    sx={{
                        width: isMobile ? "100%" : "350px",
                        display: isMobile && showChatView ? "none" : "flex",
                        flexDirection: "column",
                        height: isMobile ? "100%" : "100%",
                        minHeight: isMobile ? "auto" : "auto",
                    }}
                >
                    {/* Search Bar */}
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search contacts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Contacts List */}
                    <Box
                        ref={contactsListRef}
                        sx={{ flex: 1, overflowY: "auto" }}
                        onScroll={handleScroll}
                    >
                        {loadingRecipients && recipients.length === 0 ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%",
                                    p: 3,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {filteredContacts.map((contact, index) => (
                                <React.Fragment key={contact.id}>
                                    <ListItem
                                        button
                                        onClick={() => handleContactSelect(contact.id)}
                                        sx={{
                                            backgroundColor:
                                                selectedContact === contact.id
                                                    ? "rgba(7, 94, 84, 0.1)"
                                                    : "transparent",
                                            "&:hover": {
                                                backgroundColor: "rgba(7, 94, 84, 0.06)",
                                            },
                                            cursor: "pointer",
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Badge
                                                badgeContent={contact.unreadCount}
                                                color="primary"
                                                invisible={contact.unreadCount === 0}
                                            >
                                                <Avatar
                                                    sx={{
                                                        bgcolor: "primary.main",
                                                        width: 48,
                                                        height: 48,
                                                    }}
                                                >
                                                    <Person sx={{ fontSize: 28 }} />
                                                </Avatar>
                                            </Badge>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: "medium" }}
                                                    >
                                                        {contact.phoneNumber}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {contact.timestamp}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {contact.recipient}
                                                    </Typography>
                                            }
                                        />
                                    </ListItem>
                                    {index < filteredContacts.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                            </List>
                        )}
                        {loadingMore && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    p: 2,
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        {filteredContacts.length === 0 && !loadingRecipients && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    p: 3,
                                    color: "text.secondary",
                                }}
                            >
                                <Chat sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                                <Typography variant="body2">
                                    No recipients found
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Right Panel - Chat Messages */}
                <Paper
                    elevation={2}
                    sx={{
                        flex: 1,
                        display: isMobile && !showChatView ? "none" : "flex",
                        flexDirection: "column",
                        height: isMobile ? "100%" : "100%",
                        minHeight: isMobile ? "auto" : "auto",
                        position: "relative", // Add this for button positioning
                    }}
                >
                    {/* Chat Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: WB_UI.color.whatsappDark,
                            color: "#fff",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {isMobile && (
                                <IconButton
                                    onClick={handleBackToContacts}
                                    sx={{
                                        mr: -1,
                                        color: "#fff",
                                    }}
                                >
                                    <ArrowBack />
                                </IconButton>
                            )}
                            <Avatar
                                sx={{
                                    bgcolor: WB_UI.color.whatsappGreen,
                                    width: 48,
                                    height: 48,
                                }}
                            >
                                <Person sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: "medium", color: "#fff" }}>
                                    {recipients.find((c) => c.id === selectedContact)?.phoneNumber || "Unknown"}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                                    {recipients.find((c) => c.id === selectedContact)?.recipient || ""}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton sx={{ color: "#fff" }}>
                            <MoreVert />
                        </IconButton>
                    </Box>

                    {/* Messages Area */}
                    <Box
                        ref={messagesListRef}
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                        onScroll={handleMessagesScroll}
                    >
                        {loadingMoreMessages && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    p: 1,
                                }}
                            >
                                <CircularProgress size={20} />
                            </Box>
                        )}
                        {loadingMessages && currentMessages.length === 0 ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%",
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : currentMessages.length === 0 ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    color: "text.secondary",
                                }}
                            >
                                <Chat sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                                <Typography variant="body1">
                                    No messages yet. Start a conversation!
                                </Typography>
                            </Box>
                        ) : (
                            currentMessages.map((message) => {
                                // Check if this is a template message by checking template_id
                                if (message.is_template && message.template_id) {
                                    const templateDetails = templateDetailsCache[message.template_id];
                                    
                                    // If template details are loaded, render template message
                                    if (templateDetails) {
                                        return (
                                            <Box
                                                key={message.id}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        message.sender === "user" ? "flex-start" : "flex-end",
                                                    mb: 1,
                                                }}
                                            >
                                                <TemplateMessageBubble
                                                    templateDetails={templateDetails}
                                                    payload={message.payload}
                                                    mediaLink={message.media_link}
                                                    mediaType={message.media_type}
                                                    sender={message.sender}
                                                    timestamp={message.timestamp}
                                                />
                                            </Box>
                                        );
                                    } 
                                    // If template is still loading, show loading state
                                    else if (loadingTemplates[message.template_id]) {
                                        return (
                                            <Box
                                                key={message.id}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        message.sender === "user" ? "flex-start" : "flex-end",
                                                    mb: 1,
                                                }}
                                            >
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5,
                                                        maxWidth: "70%",
                                                        backgroundColor:
                                                            message.sender === "user"
                                                                ? "background.paper"
                                                                : "#e6eef5",
                                                        color:
                                                            message.sender === "user"
                                                                ? "text.primary"
                                                                : "white",
                                                        borderRadius: 2,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <CircularProgress size={16} />
                                                    <Typography variant="body2">
                                                        Loading template...
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        );
                                    }
                                    // If template failed to load, fall back to text message
                                    else {
                                        return (
                                            <Box
                                                key={message.id}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        message.sender === "user" ? "flex-start" : "flex-end",
                                                    mb: 1,
                                                }}
                                            >
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5,
                                                        maxWidth: "70%",
                                                        backgroundColor:
                                                            message.sender === "user"
                                                                ? "background.paper"
                                                                : "#e6eef5",
                                                        color:
                                                            message.sender === "user"
                                                                ? "text.primary"
                                                                : "white",
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    <Typography variant="body1">
                                                        {message.text || "Template message (unavailable)"}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "block",
                                                            mt: 0.5,
                                                            opacity: 0.7,
                                                            textAlign: message.sender === "user" ? "left" : "right",
                                                        }}
                                                    >
                                                        {message.timestamp}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        );
                                    }
                                }

                                // Regular text message (no template_id)
                                return (
                                    <Box
                                        key={message.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent:
                                                message.sender === "user" ? "flex-start" : "flex-end",
                                            mb: 1,
                                        }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                maxWidth: "70%",
                                                backgroundColor:
                                                    message.sender === "user"
                                                        ? "background.paper"
                                                        : "#e6eef5",
                                                color:
                                                    message.sender === "user"
                                                        ? "text.primary"
                                                        : "black",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography variant="body1">{message.text}</Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    mt: 0.5,
                                                    opacity: 0.7,
                                                    textAlign: message.sender === "user" ? "left" : "right",
                                                }}
                                            >
                                                {message.timestamp}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* Scroll to Bottom Button - Fixed position relative to chat panel */}
                    {showScrollButton && (
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 80, // Position above input area (adjust based on input height)
                                right: 16,
                                zIndex: 10,
                            }}
                        >
                            <IconButton
                                onClick={scrollToBottom}
                                sx={{
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    boxShadow: 3,
                                    "&:hover": {
                                        backgroundColor: "primary.dark",
                                    },
                                    width: 48,
                                    height: 48,
                                }}
                            >
                                <Badge
                                    badgeContent={unreadCount}
                                    color="error"
                                    invisible={unreadCount === 0}
                                    anchorOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    <KeyboardArrowDown />
                                </Badge>
                            </IconButton>
                        </Box>
                    )}

                    {/* Message Input Area */}
                    <Box
                        sx={{
                            p: 2,
                            borderTop: 1,
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            position: "relative",
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            {/* <IconButton color="primary">
                                <AttachFile />
                            </IconButton> */}
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder="Type a message..."
                                value={messageInput}
                                onChange={(e) => setMessageInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                ref={emojiButtonRef}
                                                onClick={handleEmojiButtonClick}
                                            >
                                                <EmojiEmotions />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSendMessage}
                                disabled={!messageInput.trim() || loading}
                                sx={{
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: "primary.dark",
                                    },
                                    "&.Mui-disabled": {
                                        backgroundColor: "grey.300",
                                        color: "grey.500",
                                    },
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : <Send />}
                            </IconButton>
                        </Box>
                        
                        {/* Emoji Picker */}
                        <Popper
                            open={showEmojiPicker}
                            anchorEl={emojiButtonRef.current}
                            placement="top-end"
                            sx={{
                                zIndex: 1300,
                                mb: 1,
                            }}
                        >
                            <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
                                <Paper
                                    elevation={8}
                                    sx={{
                                        p: 2,
                                        maxWidth: "280px",
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        "&::-webkit-scrollbar": {
                                            width: "8px",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "rgba(0,0,0,0.2)",
                                            borderRadius: "4px",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(8, 1fr)",
                                            gap: 0.5,
                                        }}
                                    >
                                        {professionalEmojis.map((emoji, index) => (
                                            <IconButton
                                                key={index}
                                                onClick={() => handleEmojiClick(emoji)}
                                                sx={{
                                                    fontSize: "24px",
                                                    width: "36px",
                                                    height: "36px",
                                                    padding: 0,
                                                    "&:hover": {
                                                        backgroundColor: "action.hover",
                                                    },
                                                }}
                                            >
                                                {emoji}
                                            </IconButton>
                                        ))}
                                    </Box>
                                </Paper>
                            </ClickAwayListener>
                        </Popper>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default WhatsappChatbot;

